import React, { useEffect, useState } from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Alert from "react-bootstrap/Alert";
import Spinner from "react-bootstrap/Spinner";
import { fetchPortalSettings, updatePortalSettings } from "../../services/api";
import { useToast } from "../../contexts/ToastContext";

function PortalSettingsPage() {
  const [settings, setSettings] = useState({
    permit_edit_window_days: 7,
    check_edit_window_days: 7,
    spect_edit_window_days: 7,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const { showToast } = useToast();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetchPortalSettings();
      if (response && response.settings) {
        setSettings({
          permit_edit_window_days: response.settings.permit_edit_window_days !== undefined ? response.settings.permit_edit_window_days : 7,
          check_edit_window_days: response.settings.check_edit_window_days !== undefined ? response.settings.check_edit_window_days : 7,
          spect_edit_window_days: response.settings.spect_edit_window_days !== undefined ? response.settings.spect_edit_window_days : 7,
        });
      }
    } catch (err) {
      console.error("Error loading portal settings:", err);
      setError("Failed to load portal settings.");
      showToast("Error loading portal settings", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings((prev) => ({
      ...prev,
      [name]: parseInt(value, 10) || 0,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await updatePortalSettings(settings);
      showToast("Portal settings updated successfully!", "success");
    } catch (err) {
      console.error("Error saving portal settings:", err);
      setError("Failed to save portal settings.");
      showToast("Error saving portal settings", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center my-5 py-5">
        <Spinner animation="border" role="status" variant="primary" className="mb-2" />
        <p className="text-muted">Loading portal settings...</p>
      </div>
    );
  }

  return (
    <div className="subMain-content">
      <div>
        <h1 className="mt-4">Portal Edit Lock Settings</h1>
        <p className="text-muted">Configure the time-window (in days) after submission during which applicants are allowed to edit their applications.</p>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="shadow-sm border-0" style={{ maxWidth: "600px" }}>
        <Card.Header className="bg-white border-bottom py-3">
          <h5 className="mb-0 fw-semibold text-dark">Lock Windows (Days)</h5>
        </Card.Header>
        <Card.Body className="p-4">
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3" controlId="permitLockDays">
              <Form.Label className="small text-muted mb-1 fw-medium">Simplepermit ™ Edit Lock Window</Form.Label>
              <Row className="align-items-center">
                <Col xs={8} sm={9}>
                  <Form.Control
                    type="number"
                    name="permit_edit_window_days"
                    value={settings.permit_edit_window_days}
                    onChange={handleChange}
                    min="0"
                    required
                    disabled={saving}
                  />
                </Col>
                <Col xs={4} sm={3} className="text-muted small">
                  Days
                </Col>
              </Row>
              <Form.Text className="text-muted small">
                Applicants can update SimplePermit submissions for this many days after creation.
              </Form.Text>
            </Form.Group>

            <hr className="my-4" />

            <Form.Group className="mb-3" controlId="checkLockDays">
              <Form.Label className="small text-muted mb-1 fw-medium">Simplecheck ™ Edit Lock Window</Form.Label>
              <Row className="align-items-center">
                <Col xs={8} sm={9}>
                  <Form.Control
                    type="number"
                    name="check_edit_window_days"
                    value={settings.check_edit_window_days}
                    onChange={handleChange}
                    min="0"
                    required
                    disabled={saving}
                  />
                </Col>
                <Col xs={4} sm={3} className="text-muted small">
                  Days
                </Col>
              </Row>
              <Form.Text className="text-muted small">
                Applicants can update SimpleCheck submissions for this many days after creation.
              </Form.Text>
            </Form.Group>

            <hr className="my-4" />

            <Form.Group className="mb-3" controlId="spectLockDays">
              <Form.Label className="small text-muted mb-1 fw-medium">Simplespect ™ Edit Lock Window</Form.Label>
              <Row className="align-items-center">
                <Col xs={8} sm={9}>
                  <Form.Control
                    type="number"
                    name="spect_edit_window_days"
                    value={settings.spect_edit_window_days}
                    onChange={handleChange}
                    min="0"
                    required
                    disabled={saving}
                  />
                </Col>
                <Col xs={4} sm={3} className="text-muted small">
                  Days
                </Col>
              </Row>
              <Form.Text className="text-muted small">
                Applicants can update SimpleSpect submissions for this many days after creation.
              </Form.Text>
            </Form.Group>

            <div className="d-flex justify-content-end mt-4 pt-2">
              <Button variant="outline-secondary" className="me-2" onClick={loadSettings} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={saving}>
                {saving ? "Saving..." : "Save Settings"}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
}

export default PortalSettingsPage;
